import { useCallback, useMemo, useRef } from 'react'
import { SearchItemLabel, usePopup } from '..'
import { intl } from '../../../../../i18n'
import { SelectOption } from '../../../../model/selectOption'
import ProcessScopeSearchSelectPopper from '../../../../features/ProcessScopeSearchSelectPopper/ProcessScopeSearchSelectPopper'
import { isDescendantOf } from '../../../../features/ProcessScopeSearchSelectPopper/treeUtils'

type ProcessScopeConditionItemLabelProps = {
  value: string[] | undefined
  onChange: (newValue: string[] | undefined) => void
  onClose: () => void
  search: () => void
  isLoading: boolean
  options: SelectOption[]
  parentMap: Record<string, string>
  optionsLoading: boolean
  isDeliverableSelected: boolean
  includeDeliverableOptions: boolean
  onToggleIncludeDeliverableOptions: (checked: boolean) => void
  isToggleDisabled: boolean
  onOpenPopup: () => void
  onClosePopup: () => void
}

export const ProcessScopeConditionItemLabel = ({
  value,
  onChange,
  onClose,
  search,
  isLoading,
  options,
  parentMap,
  optionsLoading,
  isDeliverableSelected,
  includeDeliverableOptions,
  onToggleIncludeDeliverableOptions,
  isToggleDisabled,
  onOpenPopup,
  onClosePopup,
}: ProcessScopeConditionItemLabelProps) => {
  const { ref, open, openPopup, closePopup } = usePopup()

  const selectedValues = useMemo(() => new Set(value || []), [value])

  const valueNames = useMemo(() => {
    return options
      .filter(op => (value || []).includes(op.value))
      .map(op => op.name)
  }, [value, options])

  const onClear = useCallback(() => {
    onChange(undefined)
    closePopup()
    onClosePopup()
    onClose()
  }, [onChange, closePopup, onClosePopup, onClose])

  const handleOpenPopup = useCallback(() => {
    onOpenPopup()
    openPopup()
  }, [onOpenPopup, openPopup])

  const handleClosePopup = useCallback(() => {
    search()
    closePopup()
    onClosePopup()
  }, [search, closePopup, onClosePopup])

  const valueRef = useRef(value)
  valueRef.current = value

  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const handleToggleItem = useCallback(
    (itemValue: string) => {
      const current = new Set(valueRef.current || [])
      if (current.has(itemValue)) {
        current.delete(itemValue)
      } else {
        current.add(itemValue)
        // 親を選択した場合、配下の選択を解除する
        for (const selected of [...current]) {
          if (
            selected !== itemValue &&
            isDescendantOf(selected, itemValue, parentMap)
          ) {
            current.delete(selected)
          }
        }
      }
      const newValue = Array.from(current)
      // 0件になっても閉じない。選び直す途中で閉じてしまわないよう、
      // 検索は他の条件と同じくポップオーバーを閉じたとき（handleClosePopup）に行う
      onChangeRef.current(newValue.length > 0 ? newValue : undefined)
    },
    [parentMap]
  )

  // 編集中は0件でもポップオーバーのアンカーとしてラベルを描画し続け、
  // 閉じた時点で0件ならラベルごと消す
  if ((!value || value.length === 0) && !open) return <></>
  return (
    <>
      <SearchItemLabel
        ref={ref}
        active={open}
        onClick={handleOpenPopup}
        onClear={onClear}
        isLoading={isLoading}
      >
        {optionsLoading ? (
          intl.formatMessage({
            id: 'wbsItemSearchFilterPanel.processScope.loading',
          })
        ) : (
          <>
            {intl.formatMessage({
              id: isDeliverableSelected
                ? 'wbsItemSearchFilterPanel.processScope.withDeliverable'
                : 'wbsItemSearchFilterPanel.processScope',
            })}
            : {valueNames.join(', ')}
          </>
        )}
      </SearchItemLabel>
      {open && !isLoading && (
        <ProcessScopeSearchSelectPopper
          open={open && !isLoading}
          anchorEl={ref.current || undefined}
          options={options}
          parentMap={parentMap}
          isLoading={optionsLoading}
          selectedValues={selectedValues}
          onToggleItem={handleToggleItem}
          onClose={handleClosePopup}
          disablePortal={false}
          includeDeliverableOptions={includeDeliverableOptions}
          onToggleIncludeDeliverableOptions={onToggleIncludeDeliverableOptions}
          isToggleDisabled={isToggleDisabled}
        />
      )}
    </>
  )
}
